import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { DataTableComponent, TableColumn, PaginationEvent } from '@shared/components/data-table/data-table.component';
import { SearchFormComponent, SearchFormData } from '@shared/components/search-form/search-form.component';
import { StatusFilterComponent, StatusOption, ActionButton } from '@shared/components/status-filter/status-filter.component';

// Interface for Third-party O2O Order
export interface ThirdPartyO2OOrder {
  id: number;
  serialNo: number;
  thirdPartyOrderNo: string;
  internalOrderNo: string;
  store: string;
  channel: string;
  flowStatus: string;
  flowFailureReason: string;
  writebackStatus: string;
  orderType: string;
  orderSource: string;
  platformStatus: string;
  refundStatus: string;
  platformReceiptStatus: string;
  paymentMethod: string;
  paymentStatus: string;
  buyerMessage: string;
  merchantRemarks: string;
  productCount: number;
  totalProductAmount: number;
  totalDiscount: number;
  totalReceivableAmount: number;
  buyerActualPayment: number;
  merchantDiscountAmount: number;
  thirdPartyDiscountAmount: number;
  shippingFee: number;
  buyerNickname: string;
  flag: string;
  recipientName: string;
  phoneNumber: string;
  shippingAddress: string;
  invoiceCategory: string;
  invoiceTitle: string;
  invoiceContent: string;
  paymentOrderNo: string;
  logisticsMethod: string;
  thirdPartyOrderTime: string;
  paymentTime: string;
  pullOrderTime: string;
  confirmReceiptTime: string;
}

// Interface for search parameters
export interface ThirdPartyO2OSearchParams {
  orderNo?: string;
  platformStatus?: string;
  refundStatus?: string;
  flowStatus?: string;
  writebackStatus?: string;
  recipientName?: string;
  salesChannel?: string;
  salesStore?: string;
  phoneNumber?: string;
  paymentTimeStart?: string;
  paymentTimeEnd?: string;
  orderTimeStart?: string;
  orderTimeEnd?: string;
  page?: number;
  pageSize?: number;
}

@Component({
  selector: 'app-third-party-o2o',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DataTableComponent,
    SearchFormComponent,
    StatusFilterComponent
  ],
  templateUrl: './third-party-o2o.component.html',
  styleUrl: './third-party-o2o.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThirdPartyO2OComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private translateService = inject(TranslateService);

  // Signals for dropdown options
  platformStatusOptions = signal<string[]>([]);
  refundStatusOptions = signal<string[]>([]);
  flowStatusOptions = signal<string[]>([]);
  writebackStatusOptions = signal<string[]>([]);
  salesChannels = signal<string[]>([]);
  salesStores = signal<string[]>([]);

  // Table data
  selectedOrders = signal<ThirdPartyO2OOrder[]>([]);
  loading = signal(false);
  orders = signal<ThirdPartyO2OOrder[]>([]);
  totalRecords = signal(0);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(20);
  first = signal(0);

  // Status filter options
  statusOptions: StatusOption[] = [
    { key: 'all', label: this.translateService.instant('third_party.o2o.status.all'), active: true },
    { key: 'pending', label: this.translateService.instant('third_party.o2o.status.pending'), active: false },
    { key: 'processing', label: this.translateService.instant('third_party.o2o.status.processing'), active: false },
    { key: 'completed', label: this.translateService.instant('third_party.o2o.status.completed'), active: false },
    { key: 'failed', label: this.translateService.instant('third_party.o2o.status.failed'), active: false }
  ];

  // Action buttons
  primaryButton: ActionButton = {
    label: this.translateService.instant('third_party.o2o.actions.create_order'),
    severity: 'info',
    disabled: false,
    show: true,
    icon: 'add'
  };

  secondaryButton: ActionButton = {
    label: this.translateService.instant('third_party.o2o.actions.download_orders'),
    severity: 'info',
    outlined: true,
    disabled: false,
    show: true,
    icon: 'download'
  };

  // Table columns for DataTableComponent
  tableColumns: TableColumn[] = [
    { label: this.translateService.instant('third_party.o2o.table.marketplace_order_no'), prop: 'thirdPartyOrderNo', width: 150 },
    { label: this.translateService.instant('third_party.o2o.table.internal_order_no'), prop: 'internalOrderNo', width: 120 },
    { label: this.translateService.instant('third_party.o2o.table.store'), prop: 'store', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.channel'), prop: 'channel', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.flow_status'), prop: 'flowStatus', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.flow_failure_reason'), prop: 'flowFailureReason', width: 150 },
    { label: this.translateService.instant('third_party.o2o.table.writeback_status'), prop: 'writebackStatus', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.order_type'), prop: 'orderType', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.order_source'), prop: 'orderSource', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.platform_status'), prop: 'platformStatus', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.refund_status'), prop: 'refundStatus', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.platform_receipt_status'), prop: 'platformReceiptStatus', width: 120 },
    { label: this.translateService.instant('third_party.o2o.table.payment_method'), prop: 'paymentMethod', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.payment_status'), prop: 'paymentStatus', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.buyer_message'), prop: 'buyerMessage', width: 120 },
    { label: this.translateService.instant('third_party.o2o.table.merchant_remarks'), prop: 'merchantRemarks', width: 120 },
    { label: this.translateService.instant('third_party.o2o.table.product_count'), prop: 'productCount', width: 80 },
    { label: this.translateService.instant('third_party.o2o.table.total_product_amount'), prop: 'totalProductAmount', width: 120 },
    { label: this.translateService.instant('third_party.o2o.table.total_discount'), prop: 'totalDiscount', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.total_receivable_amount'), prop: 'totalReceivableAmount', width: 120 },
    { label: this.translateService.instant('third_party.o2o.table.buyer_actual_payment'), prop: 'buyerActualPayment', width: 120 },
    { label: this.translateService.instant('third_party.o2o.table.merchant_discount_amount'), prop: 'merchantDiscountAmount', width: 140 },
    { label: this.translateService.instant('third_party.o2o.table.third_party_discount_amount'), prop: 'thirdPartyDiscountAmount', width: 140 },
    { label: this.translateService.instant('third_party.o2o.table.shipping_fee'), prop: 'shippingFee', width: 80 },
    { label: this.translateService.instant('third_party.o2o.table.buyer_nickname'), prop: 'buyerNickname', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.flag'), prop: 'flag', width: 80 },
    { label: this.translateService.instant('third_party.o2o.table.recipient_name'), prop: 'recipientName', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.phone_number'), prop: 'phoneNumber', width: 120 },
    { label: this.translateService.instant('third_party.o2o.table.shipping_address'), prop: 'shippingAddress', width: 150 },
    { label: this.translateService.instant('third_party.o2o.table.invoice_category'), prop: 'invoiceCategory', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.invoice_title'), prop: 'invoiceTitle', width: 120 },
    { label: this.translateService.instant('third_party.o2o.table.invoice_content'), prop: 'invoiceContent', width: 120 },
    { label: this.translateService.instant('third_party.o2o.table.payment_order_no'), prop: 'paymentOrderNo', width: 120 },
    { label: this.translateService.instant('third_party.o2o.table.logistics_method'), prop: 'logisticsMethod', width: 100 },
    { label: this.translateService.instant('third_party.o2o.table.marketplace_order_time'), prop: 'thirdPartyOrderTime', width: 150 },
    { label: this.translateService.instant('third_party.o2o.table.payment_time'), prop: 'paymentTime', width: 150 },
    { label: this.translateService.instant('third_party.o2o.table.pull_order_time'), prop: 'pullOrderTime', width: 150 },
    { label: this.translateService.instant('third_party.o2o.table.confirm_receipt_time'), prop: 'confirmReceiptTime', width: 150 }
  ];

  // Computed properties
  canCreateOrder = computed(() => true);
  canDownloadOrders = computed(() => true);

  // Dialog state
  showCreateOrderDialog = signal(false);
  showDownloadDialog = signal(false);

  ngOnInit(): void {
    this.loadDropdownOptions();
    this.loadOrders();
    this.updateButtonStates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadDropdownOptions(): void {
    // Set static options for now - in real app these would come from API
    this.platformStatusOptions.set(['All', 'Pending', 'Processing', 'Completed', 'Failed']);
    this.refundStatusOptions.set(['All', 'No Refund', 'Refunded', 'Partial Refund']);
    this.flowStatusOptions.set(['All', 'Pending', 'Processing', 'Completed', 'Failed']);
    this.writebackStatusOptions.set(['All', 'Pending', 'Completed', 'Failed']);
    this.salesChannels.set(['All', 'Facebook', 'Shopee', 'Tmall']);
    this.salesStores.set(['All', 'Happy Selection Online Store', 'Colorful Good Goods Warehouse', 'Trend Station']);
  }

  private loadOrders(): void {
    this.loading.set(true);
    
    // Mock data for demonstration
    const mockOrders: ThirdPartyO2OOrder[] = [
      {
        id: 1,
        serialNo: 1,
        thirdPartyOrderNo: 'TP001234567',
        internalOrderNo: 'IN001234567',
        store: 'TikTok Store',
        channel: 'TikTok',
        flowStatus: 'Completed',
        flowFailureReason: '',
        writebackStatus: 'Completed',
        orderType: 'Normal',
        orderSource: 'Platform',
        platformStatus: 'Completed',
        refundStatus: 'No Refund',
        platformReceiptStatus: 'Received',
        paymentMethod: 'Online Payment',
        paymentStatus: 'Paid',
        buyerMessage: 'Please deliver quickly',
        merchantRemarks: 'High priority order',
        productCount: 2,
        totalProductAmount: 299.00,
        totalDiscount: 20.00,
        totalReceivableAmount: 279.00,
        buyerActualPayment: 279.00,
        merchantDiscountAmount: 10.00,
        thirdPartyDiscountAmount: 10.00,
        shippingFee: 0.00,
        buyerNickname: 'buyer123',
        flag: 'Normal',
        recipientName: 'John',
        phoneNumber: '1381234',
        shippingAddress: 'HCM City, Vietnam',
        invoiceCategory: 'Personal',
        invoiceTitle: 'Personal',
        invoiceContent: 'Electronics',
        paymentOrderNo: 'PAY001234567',
        logisticsMethod: 'Express',
        thirdPartyOrderTime: '2024-01-15 10:30:00',
        paymentTime: '2024-01-15 10:35:00',
        pullOrderTime: '2024-01-15 10:40:00',
        confirmReceiptTime: '2024-01-18 14:20:00'
      }
    ];

    // Simulate API call
    setTimeout(() => {
      this.orders.set(mockOrders);
      this.totalRecords.set(mockOrders.length);
      this.loading.set(false);
    }, 500);
  }

  private getActiveStatus(): 'all' | 'pending' | 'processing' | 'completed' | 'failed' {
    const activeStatus = this.statusOptions.find(status => status.active);
    return (activeStatus ? activeStatus.key : 'all') as 'all' | 'pending' | 'processing' | 'completed' | 'failed';
  }

  private updateButtonStates(): void {
    this.primaryButton.disabled = !this.canCreateOrder();
    this.secondaryButton.disabled = !this.canDownloadOrders();
  }

  onSearch(searchData: SearchFormData): void {
    this.currentPage.set(1);
    this.first.set(0);
    // Convert search data to search params and load orders
    this.loadOrders();
  }

  onReset(): void {
    this.selectedOrders.set([]);
    this.currentPage.set(1);
    this.first.set(0);
    this.loadOrders();
    this.updateButtonStates();
  }

  onPageChange(event: PaginationEvent): void {
    this.currentPage.set(event.page! + 1);
    this.first.set(event.first || 0);
    this.loadOrders();
  }

  onStatusChange(status: string): void {
    // Update status options
    this.statusOptions = this.statusOptions.map(option => ({
      ...option,
      active: option.key === status
    }));
    
    this.currentPage.set(1);
    this.first.set(0);
    this.loadOrders();
  }

  onSelectionChange(selection: ThirdPartyO2OOrder[]): void {
    this.selectedOrders.set(selection);
    this.updateButtonStates();
  }

  onCreateOrder(): void {
    this.showCreateOrderDialog.set(true);
  }

  onDownloadOrders(): void {
    this.showDownloadDialog.set(true);
  }

  confirmCreateOrder(): void {
    // Handle create order logic
    this.showCreateOrderDialog.set(false);
  }

  cancelCreateOrder(): void {
    this.showCreateOrderDialog.set(false);
  }

  confirmDownloadOrders(): void {
    // Handle download orders logic
    this.showDownloadDialog.set(false);
  }

  cancelDownloadOrders(): void {
    this.showDownloadDialog.set(false);
  }

  refreshData(): void {
    this.loadOrders();
  }
} 